import { GroupCartVersionConflict } from "@/api/group-cart";

/**
 * Group-cart writes carry If-Match. With several guests at one table, another
 * guest's write routinely lands between our last SSE update and our POST, so a
 * 412 is the normal case, not an error: the server hands back its cart, we
 * resync to it and re-send at its version (adds always still apply; edits only
 * while their line still exists — [canRetry]).
 */
export async function withVersionRetry<T>(
  version: number,
  run: (version: number) => Promise<T>,
  opts: {
    onConflict: (c: GroupCartVersionConflict) => void;
    canRetry?: (c: GroupCartVersionConflict) => boolean;
    attempts?: number;
    sleep?: (ms: number) => Promise<void>;
  },
): Promise<T> {
  // A busy table (several guests editing at once) loses the race several
  // times in a row; jittered backoff stops racing writers colliding in
  // lockstep (F25: a dropped quantity change silently snapped back).
  const attempts = opts.attempts ?? 6;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  let v = version;
  for (let i = 1; ; i++) {
    try {
      return await run(v);
    } catch (err) {
      if (!(err instanceof GroupCartVersionConflict)) throw err;
      opts.onConflict(err);
      if (i >= attempts || (opts.canRetry && !opts.canRetry(err))) throw err;
      v = err.currentVersion;
      await sleep(50 * i + Math.floor(Math.random() * 150));
    }
  }
}

/** A fire that lost the version race still applies while the cart has lines
 *  to send; an empty cart means another guest already fired it (F27). */
export const canRefire = (c: GroupCartVersionConflict): boolean =>
  c.cart.draftLines.length > 0;
